import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { useMutation } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormText from 'components/KNForm/KNFormText'
import { analyticsEvent } from 'global/helpers/analytics'
import { processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { updateTripLegsCacheWithUpdatedLegs } from 'screens/StatusManager/StatusManager.helpers'
import { TRIPS_QUERY_KEY } from 'screens/TripDashboard/TripDashboard.service'
import type { TripData } from 'screens/TripDashboard/TripDashboard.types'
import { TRIP_DETAILS_QUERY_KEY, tripLegsQueryOptions, updateProReference } from 'screens/TripDetails/TripDetails.service'
import type { LegData, ProReferenceFormValues } from 'screens/TripDetails/TripDetails.types'

interface ProReferenceDialogPayload {
  trip: TripData
  leg: LegData
  weblinkToken?: string
}

interface ProReferenceDialogProps {
  payload: ProReferenceDialogPayload
  open: boolean
  onAction: () => void
  onClose: () => void
}

export const getUpdatedLegForUpdateProReference = (
  leg: LegData,
  data: ProReferenceFormValues,
): LegData => ({
  ...leg,
  attributes: {
    ...leg.attributes,
    shipmentReferences: leg.attributes.shipmentReferences.map((item) =>
      item.code === 'AAK' ? { ...item, value: data.proReference } : item,
    ),
  },
})

const ProReferenceDialog = ({ payload, open, onAction, onClose }: ProReferenceDialogProps): ReactElement => {
  useTheme()
  const { t } = useTranslation()
  const { handleSubmit, reset, control, formState, setError } = useForm<ProReferenceFormValues>({
    criteriaMode: 'all',
  })
  const updateProReferenceMutation = useMutation({
    mutationFn: (data: ProReferenceFormValues) =>
      updateProReference(payload.trip.entityId, payload.leg.shipmentNumber, data, payload.weblinkToken),
    onSuccess: async (_response, data, _result, context) => {
      const updatedLeg = getUpdatedLegForUpdateProReference(payload.leg, data)
      const queryKey = tripLegsQueryOptions(payload.trip.entityId, payload.weblinkToken).queryKey
      await updateTripLegsCacheWithUpdatedLegs([updatedLeg], queryKey, context.client)
      setTimeout(() => {
        void Promise.all([
          context.client.invalidateQueries({ queryKey: [TRIP_DETAILS_QUERY_KEY, payload.trip.entityId] }),
          context.client.invalidateQueries({ queryKey: [TRIPS_QUERY_KEY] }),
        ])
      }, 2000)
    },
    onError: (error) => setError('root', processServerErrorMessages(error)),
  })

  useEffect(() => {
    const proReference = payload.leg.attributes.shipmentReferences.find(item => item.code === 'AAK')
    reset(
      processDefaultValues({
        proReference: proReference?.value,
      }),
    )
  }, [payload])

  const onSubmit: SubmitHandler<ProReferenceFormValues> = async (data: ProReferenceFormValues) => {
    await updateProReferenceMutation.mutateAsync(data)
    onAction()
    analyticsEvent('polestar_cs_pro_updated')
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      maxWidth="xs"
      actions={
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          loading={formState.isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {t('screens.cs.trip_details.pro.update_pro')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Stack spacing={3} alignItems="center">
        <Typography variant="h6">{t('screens.cs.trip_details.pro.pro_reference_statement')}</Typography>
        <KNFormText
          name="proReference"
          label={t('screens.cs.trip_details.pro.pro_reference')}
          control={control}
          rows={1}
          sx={{ width: '25rem' }}
          rules={{
            required: t('form.validation.required'),
          }}
        />
      </Stack>
    </KNDialog>
  )
}

export default ProReferenceDialog
